import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Input, FormGroup, Label } from "reactstrap";
import Select from "react-select";
import swal from "@sweetalert/with-react";
import NumberFormat from "react-number-format";
import { baseurl } from "../../../baseurl.js";
import axios from "axios";
import { Link } from "react-router-dom";
import moment from "moment";
import CreatableSelect from "react-select/creatable";
import ResponseLoader from "../ShortCutComponents/ResponseLoader.jsx";
import { checkPermission, getBulkItemQtyAmt } from "../../../Helper.js";
import { Modal } from "react-bootstrap";

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const ROUTE_FORM = "/bomForm";
const ROUTE_OVERVIEW = "/bomOverview";

// Backend endpoints - apne actual routes se match kar lein.
const API = {
  techPackLov: `${baseurl}/pointofsale/getTechPackOverview`,
  componentLov: `${baseurl}/lovs/getComponents`,
  operationLov: `${baseurl}/servicePurchase/getSetup`,
  itemPartLov: `${baseurl}/lovs/getBasisLov`,
  mainItemLov: `${baseurl}/PayRoll/getEmpLovs`, // fabric aur trim dono isi se
  itemLov: `${baseurl}/lovs/getItem`,
  getById: `${baseurl}/pointofsale/getBomWithId`,
  add: `${baseurl}/pointofsale/addBom`,
  update: `${baseurl}/pointofsale/updateBom`,
};

const HEAD_STYLE = { background: "#1f3864", color: "#fff", fontSize: "12px" };
const SECTION_TITLE_STYLE = {
  color: "#1f3864",
  fontWeight: "bold",
  margin: "18px 0 8px 0",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Item LOV (/lovs/getItem) ki ID `item_id` me aati hai; baaki LOVs me `id`.
const optId = (o) => o?.item_id ?? o?.id ?? o?.value ?? null;

const toNum = (v) =>
  v === null || v === undefined || v === "" || !Number.isFinite(Number(v))
    ? null
    : Number(v);

// checkNullObject: aapka helper. Select ki selected value ka object; id na ho
// to null (Select khali dikhta hai).
const checkNullObject = (objId, objLabel) => {
  return objId ? { id: objId, label: objLabel } : null;
};

// Agar query me description column na aaye to label/uom LOV se le lete hain.
const lovOption = (list, id) =>
  (list || []).find((o) => String(optId(o)) === String(id));

const fmtMoney = (n) =>
  (Number.isFinite(n) ? n : 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Ek row ka planned amount/suit = consumption (ya rate seedha, oheads ke lie) * rate
const rowAmount = (qtyPerFg, rate) =>
  (toNum(qtyPerFg) || 0) * (toNum(rate) || 0);

// Backend se jo bhi aaye (array / null / undefined), use safely array bana dete hain
const asArray = (value) => (Array.isArray(value) ? value : []);

// ---------------------------------------------------------------------------
// Error handling helpers
// ---------------------------------------------------------------------------

// Axios call ko wrap karta hai: error throw karne ke bajaye { res, error }
// return karta hai, take ek LOV fail hone par baaki data load hote rahe.
const safePost = async (url, requestBody, config, label) => {
  try {
    const res = await axios.post(url, requestBody, config);
    return { res, error: null, label };
  } catch (error) {
    console.error(`BOM form - ${label} load failed`, error);
    return { res: null, error, label };
  }
};

// Row level validation errors ki keys, e.g. "fabrics-0-component"
const errKey = (section, index, name) => `${section}-${index}-${name}`;

// Backend ka message / HTTP status / timeout / network - sab ek hi jagah se
// user-friendly text me convert hota hai, take khaali ya "undefined" alert na aaye.
const HTTP_STATUS_MESSAGES = {
  400: "Invalid request. Please check the entered data.",
  401: "Your session has expired. Please login again.",
  403: "You are not allowed to perform this action.",
  404: "Requested record was not found.",
  408: "Request timed out. Please try again.",
  409: "This record is currently in use. Please refresh and try again.",
  500: "Internal server error. Please contact your administrator.",
  502: "Server is temporarily unavailable. Please try again.",
  503: "Service is temporarily unavailable. Please try again.",
  504: "Server did not respond in time. Please try again.",
};

const getApiErrorMessage = (error, fallback = "Something went wrong!") => {
  if (!error) return fallback;

  const data = error.response?.data;

  // Account restricted (backend ka special code)
  if (data?.code === "PAYMENT_PENDING") {
    return "Account is Restricted. For further queries, contact Accmen Team";
  }

  // Sabse pehle backend ka apna message
  let serverMessage = "";
  if (typeof data === "string") serverMessage = data;
  else if (typeof data?.message === "string") serverMessage = data.message;
  else if (typeof data?.error === "string") serverMessage = data.error;
  if (serverMessage.trim()) return serverMessage.trim();

  const status = error.response?.status;
  if (status) {
    return (
      HTTP_STATUS_MESSAGES[status] || `Request failed with status ${status}.`
    );
  }

  if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
    return "Request timed out. Please check your connection and try again.";
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "No internet connection. Please check your network.";
  }
  if (error.message === "Network Error" || (error.request && !error.response)) {
    return "Server is not reachable. Please check your connection and try again.";
  }

  return error.message || fallback;
};

// Invalid field par red border (Input / NumberFormat ke lie)
const fieldErrorStyle = (hasError) =>
  hasError
    ? {
        borderColor: "#dc3545",
        boxShadow: "0 0 0 0.2rem rgba(220, 53, 69, 0.25)",
      }
    : undefined;

// Invalid field par red border (react-select ke lie)
const selectErrorStyles = (hasError) =>
  hasError
    ? {
        control: (base) => ({
          ...base,
          borderColor: "#dc3545",
          boxShadow: "0 0 0 0.2rem rgba(220, 53, 69, 0.25)",
        }),
      }
    : undefined;

const emptyFabRow = () => ({
  id: null,
  component: null,
  fabric: null,
  qtyPerFg: null,
  uom: "",
  rate: null,
});
const emptyTrimRow = () => ({
  id: null,
  item: null,
  specDet: "",
  qtyPerFg: null,
  uom: "",
  rate: null,
});
const emptyOheadRow = () => ({
  id: null,
  operation: null,
  itemPart: [],
  resource: null,
  estDays: null,
  qty: null,
  unitCost: null,
  ratePerFg: null,
});
const emptyRemarksRow = () => ({
  id: null,
  remarks: "",
});

const emptyData = () => ({
  id: null,
  techPack: null, // { id, label, article, ver_no, no_of_pcs }
  fabrics: [emptyFabRow()],
  trims: [emptyTrimRow()],
  oheads: [emptyOheadRow()],
  remarksList: [emptyRemarksRow()],
});

// Row ke add/remove icons (same icons jo Meat Marination/Tech Pack forms me hain)
const RowActions = ({ onAdd, onRemove }) => (
  <td style={{ width: "60px", whiteSpace: "nowrap", verticalAlign: "middle" }}>
    <i
      className="fa fa-plus"
      style={{ color: "green", cursor: "pointer" }}
      onClick={onAdd}
    ></i>
    <i
      className="fa fa-trash ml-2"
      style={{ color: "red", cursor: "pointer" }}
      onClick={onRemove}
    ></i>
  </td>
);

// ---------------------------------------------------------------------------
// Print
// Sirf view mode (type === 1) me Print button dikhta hai. Screen wala form print
// me hide hota hai (.tp-no-print) aur ye plain sheet dikhti hai (.tp-print-only).
// Rows data se banti hain, is liye jitni rows hongi utni hi print hongi; page
// lamba ho to table header har page par repeat hota hai.
// ---------------------------------------------------------------------------
const PRINT_CSS = `
.tp-print-only { display: none; }
@media print {
  @page {
    size: A4 portrait;
    margin: 8mm 8mm 10mm 8mm;
  }
  body, html {
    background: #fff !important;
    color: #000 !important;
    font-size: 11px;
    margin: 0 !important;
    padding: 0 !important;
  }
  .tp-no-print { display: none !important; }
  .tp-print-only { display: block !important; }
  footer, .app-footer, .footer, header, .app-header, .sidebar, .breadcrumb, nav {
    display: none !important;
  }

  .tp-sheet {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 11px;
    color: #111;
    width: 100%;
    margin: 0;
    padding: 0;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .tp-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    border-bottom: 2.5px solid #1f3864;
    padding-bottom: 6px;
    margin-bottom: 10px;
  }
  .tp-title {
    font-size: 17px;
    font-weight: 800;
    color: #1f3864;
    letter-spacing: 0.8px;
    text-transform: uppercase;
  }
  .tp-meta {
    font-size: 11px;
    color: #444;
    text-align: right;
  }
  .tp-meta strong {
    color: #1f3864;
  }

  .tp-section {
    margin-top: 10px;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .tp-section-title {
    font-size: 11px;
    font-weight: 700;
    color: #1f3864;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 4px 0;
    break-after: avoid;
    page-break-after: avoid;
  }

  .tp-identity {
    display: flex;
    flex-wrap: wrap;
    border: 1px solid black;
    background: #fff;
  }
  .tp-identity > div {
    flex: 1 1 20%;
    min-width: 110px;
    padding: 5px 8px;
    border-right: 2px solid black;
    border-bottom: 2px solid black;
    box-sizing: border-box;
  }
  .tp-identity > div:last-child {
    border-right: 0;
  }
  .tp-identity .tp-wide {
    flex: 1 1 100%;
    border-right: 0;
  }
  .tp-label {
    font-size: 11px;
    text-transform: uppercase;
    color: #666;
    margin-bottom: 2px;
    font-weight: 600;
  }
  .tp-value {
    font-size: 13px;
    font-weight: 700;
    color: #111;
    min-height: 14px;
    word-break: break-word;
  }

  .tp-sheet table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 4px;
  }
  .tp-sheet thead {
    display: table-header-group;
  }
  .tp-sheet tr {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .tp-sheet th {
    background: #eef1f7 !important;
    color: #1f3864;
    font-size: 10px;
    font-weight: 700;
    text-align: left;
    padding: 5px 6px;
    border-top: 1.5px solid #1f3864;
    border-bottom: 1.5px solid #1f3864;
    border-right: 1px solid black;
    text-transform: uppercase;
  }
  .tp-sheet th:last-child {
    border-right: 0;
  }
  .tp-sheet td {
    padding: 4px 6px;
    border-bottom: 1px solid black;
    border-right: 1px solid black;
    font-size: 10px;
    vertical-align: middle;
  }
  .tp-sheet td:last-child {
    border-right: 0;
  }
  .tp-sheet .tp-r { text-align: right; }
  .tp-sheet .tp-c { text-align: center; }
  .tp-sheet .tp-empty { text-align: center; color: #888; font-style: italic; }
  .tp-sheet .tp-subtotal td {
    font-weight: bold;
    background: #f8f9fa;
    border-top: 1px solid black;
    border-bottom: 1.5px solid #1f3864;
  }
  .tp-sheet .tp-grand td {
    font-weight: bold;
    font-size: 11.5px;
    border-top: 1.5px solid #1f3864;
    border-bottom: 1.5px solid #1f3864;
    background: #eef1f7 !important;
    color: #1f3864;
  }

  .tp-summary {
    width: 60%;
    margin-left: auto;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .tp-signatures {
    display: flex;
    justify-content: space-between;
    gap: 28px;
    margin-top: 36px;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .tp-signatures > div {
    flex: 1;
    text-align: center;
  }
  .tp-sign-line {
    border-top: 1px solid #444;
    margin-top: 24px;
  }
  .tp-sign-label {
    font-size: 9.5px;
    color: #444;
    font-weight: 700;
    margin-top: 4px;
    text-transform: uppercase;
  }

  .tp-foot {
    display: flex;
    justify-content: space-between;
    font-size: 9px;
    color: #666;
    margin-top: 18px;
    padding-top: 6px;
    border-top: 1px solid black;
    break-inside: avoid;
    page-break-inside: avoid;
  }
}
`;

const fmtQty = (v) =>
  toNum(v) === null
    ? "–"
    : toNum(v).toLocaleString("en-US", { maximumFractionDigits: 3 });

const printStamp = () => moment().format("DD/MM/YYYY, HH:mm:ss");

const EmptyRow = ({ cols }) => (
  <tr>
    <td colSpan={cols} className="tp-empty">
      No records
    </td>
  </tr>
);

const PrintSheet = ({
  data,
  oheadRows,
  plannedQty,
  fabricTotal,
  trimTotal,
  grandTotalPerSuit,
}) => {
  // Khali (unselected) rows print nahi hoti
  const fabRows = data.fabrics.filter((r) => r.component || r.fabric);
  const trimRows = data.trims.filter((r) => r.item);
  const costRows = oheadRows.filter((r) => r.operation);
  const verNo = data.techPack?.ver_no;

  return (
    <div className="tp-print-only tp-sheet">
      <div className="tp-head">
        <div>
          <div className="tp-title">ZEENAT-PRET</div>
          <div className="tp-title" style={{ fontSize: "15px", color: "#1f3864" }}>
            BOM &amp; PRE-COST SPECIFICATION
          </div>
          <div style={{ fontSize: "12px", color: "#333", marginTop: "2px", fontWeight: "600" }}>
            {data.techPack?.label || "Bill of Materials"}
          </div>
        </div>
        <div className="tp-meta">
          <div><strong>BOM Doc #:</strong> #{String(data.id ?? "").padStart(3, "0")}</div>
          <div><strong>Tech Pack:</strong> {data.techPack?.label || "–"}{verNo ? ` (v${verNo})` : ""}</div>
          <div><strong>Planned Suit Qty:</strong> {plannedQty ? Number(plannedQty).toLocaleString() : "—"}</div>
        </div>
      </div>

      <div className="tp-section" style={{ marginTop: 0 }}>
        <div className="tp-section-title">Identity &amp; Order Information</div>
        <div className="tp-identity">
          <div>
            <div className="tp-label">BOM ID</div>
            <div className="tp-value">#{String(data.id ?? "").padStart(3, "0")}</div>
          </div>
          <div>
            <div className="tp-label">Tech Pack (Article • Version)</div>
            <div className="tp-value">{data.techPack?.label || "–"}{verNo ? ` • v${verNo}` : ""}</div>
          </div>
          <div>
            <div className="tp-label">Planned Suit Qty</div>
            <div className="tp-value">{plannedQty ? Number(plannedQty).toLocaleString() : "—"}</div>
          </div>
          <div>
            <div className="tp-label">Pre-Cost / Suit</div>
            <div className="tp-value">Rs. {fmtMoney(grandTotalPerSuit)}</div>
          </div>
          <div>
            <div className="tp-label">Total Planned Cost</div>
            <div className="tp-value">Rs. {fmtMoney(grandTotalPerSuit * (plannedQty || 0))}</div>
          </div>
        </div>
      </div>

      <div className="tp-section">
        <div className="tp-section-title">Raw Fabric</div>
        <table>
          <thead>
            <tr>
              <th style={{ width: "22%" }}>Component</th>
              <th style={{ width: "32%" }}>Fabric Stuff</th>
              <th className="tp-r" style={{ width: "14%" }}>Planned Cons / Suit</th>
              <th className="tp-c" style={{ width: "8%" }}>UOM</th>
              <th className="tp-r" style={{ width: "10%" }}>Rate</th>
              <th className="tp-r" style={{ width: "14%" }}>Planned Amount</th>
            </tr>
          </thead>
          <tbody>
            {fabRows.length === 0 && <EmptyRow cols={6} />}
            {fabRows.map((row, index) => (
              <tr key={index}>
                <td>{row.component?.label}</td>
                <td>{row.fabric?.label}</td>
                <td className="tp-r">{fmtQty(row.qtyPerFg)}</td>
                <td className="tp-c">{row.uom}</td>
                <td className="tp-r">Rs. {fmtMoney(toNum(row.rate) || 0)}</td>
                <td className="tp-r">
                  Rs. {fmtMoney(rowAmount(row.qtyPerFg, row.rate))}
                </td>
              </tr>
            ))}
            <tr className="tp-subtotal">
              <td colSpan={5} className="tp-r">
                Sub-total — Raw Fabric
              </td>
              <td className="tp-r">Rs. {fmtMoney(fabricTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="tp-section">
        <div className="tp-section-title">Trims / Accessories</div>
        <table>
          <thead>
            <tr>
              <th style={{ width: "22%" }}>Material</th>
              <th style={{ width: "32%" }}>Specification</th>
              <th className="tp-r" style={{ width: "14%" }}>Planned Cons / Suit</th>
              <th className="tp-c" style={{ width: "8%" }}>UOM</th>
              <th className="tp-r" style={{ width: "10%" }}>Rate</th>
              <th className="tp-r" style={{ width: "14%" }}>Planned Amount</th>
            </tr>
          </thead>
          <tbody>
            {trimRows.length === 0 && <EmptyRow cols={6} />}
            {trimRows.map((row, index) => (
              <tr key={index}>
                <td>{row.item?.label}</td>
                <td>{row.specDet}</td>
                <td className="tp-r">{fmtQty(row.qtyPerFg)}</td>
                <td className="tp-c">{row.uom}</td>
                <td className="tp-r">Rs. {fmtMoney(toNum(row.rate) || 0)}</td>
                <td className="tp-r">
                  Rs. {fmtMoney(rowAmount(row.qtyPerFg, row.rate))}
                </td>
              </tr>
            ))}
            <tr className="tp-subtotal">
              <td colSpan={5} className="tp-r">
                Sub-total — Trims / Accessories
              </td>
              <td className="tp-r">Rs. {fmtMoney(trimTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="tp-section">
        <div className="tp-section-title">
          Process Cost Heads (Planned Rate / Suit)
        </div>
        <table>
          <thead>
            <tr>
              <th style={{ width: "32%" }}>Cost Head</th>
              <th style={{ width: "28%" }}>Basis / Part</th>
              <th className="tp-r" style={{ width: "18%" }}>Planned Rate / Suit</th>
              <th className="tp-r" style={{ width: "22%" }}>Planned Amount (× {plannedQty || 0})</th>
            </tr>
          </thead>
          <tbody>
            {costRows.length === 0 && <EmptyRow cols={4} />}
            {costRows.map((row, index) => (
              <tr key={index}>
                <td>{row.operation?.label}</td>
                <td>{row.itemPart?.label}</td>
                <td className="tp-r">Rs. {fmtMoney(row.amountPerSuit)}</td>
                <td className="tp-r">
                  Rs. {fmtMoney(row.amountPerSuit * (plannedQty || 0))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="tp-section tp-summary">
        <div className="tp-section-title">
          Pre-Cost Summary (Planned Suit Qty: {plannedQty || 0})
        </div>
        <table>
          <thead>
            <tr>
              <th>Cost Head</th>
              <th className="tp-r">Amount / Suit</th>
              <th className="tp-r">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Raw Fabric</td>
              <td className="tp-r">Rs. {fmtMoney(fabricTotal)}</td>
              <td className="tp-r">Rs. {fmtMoney(fabricTotal * (plannedQty || 0))}</td>
            </tr>
            <tr>
              <td>Trims / Accessories</td>
              <td className="tp-r">Rs. {fmtMoney(trimTotal)}</td>
              <td className="tp-r">Rs. {fmtMoney(trimTotal * (plannedQty || 0))}</td>
            </tr>
            {costRows.map((r, i) => (
              <tr key={i}>
                <td>{r.operation?.label}</td>
                <td className="tp-r">Rs. {fmtMoney(r.amountPerSuit)}</td>
                <td className="tp-r">
                  Rs. {fmtMoney(r.amountPerSuit * (plannedQty || 0))}
                </td>
              </tr>
            ))}
            <tr className="tp-grand">
              <td>Grand Total (Pre-Cost)</td>
              <td className="tp-r">Rs. {fmtMoney(grandTotalPerSuit)}</td>
              <td className="tp-r">
                Rs. {fmtMoney(grandTotalPerSuit * (plannedQty || 0))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* <div className="tp-signatures">
        <div>
          <div className="tp-sign-line"></div>
          <div className="tp-sign-label">Prepared By</div>
        </div>
        <div>
          <div className="tp-sign-line"></div>
          <div className="tp-sign-label">Reviewed By</div>
        </div>
        <div>
          <div className="tp-sign-line"></div>
          <div className="tp-sign-label">Approved By</div>
        </div>
      </div> */}

      <div className="tp-foot">
        <span>
          BOM #{String(data.id ?? "").padStart(3, "0")}
          {verNo ? ` • Tech Pack v${verNo}` : ""}
        </span>
        <span>
          Printed at <span className="tp-printed-at">{printStamp()}</span>
        </span>
      </div>
    </div>
  );
};

export default function BOMForm(props) {
  var username = localStorage.getItem("username");
  var token = localStorage.getItem("token");
  var companyId = localStorage.getItem("companyId");

  const [loadingResponce, setLoadingResponce] = useState(false);
  const [isSavedLocked, setIsSavedLocked] = useState(false);
  // Validation errors: { "fabrics-0-component": true, ... } - invalid fields
  // ko red border milta hai.
  const [rowErrors, setRowErrors] = useState({});
  const [data, setData] = useState(emptyData());
  const [techPackLov, setTechPackLov] = useState([]);
  const [componentLov, setComponentLov] = useState([]);
  const [operationLov, setOperationLov] = useState([]);
  const [itemPartLov, setItemPartLov] = useState([]);
  const [mainItemLov, setMainItemLov] = useState([]);
  const [resourceLov, setResourceLov] = useState([]);
  const [itemLov, setItemLov] = useState([]);
  const [remarksModalOpen, setRemarksModalOpen] = useState(false);

  // View mode (type === 1) me poora form read-only rehta hai. Save hone ke baad
  // bhi lock kar dete hain take dobara submit na ho jaye.
  const isViewOnly = props.location.state?.type === 1;
  const isLocked = isViewOnly || isSavedLocked;
  const isEdit = data.id !== null;

  const printSheet = () => {
    // "Save as PDF" file name document title se banta hai
    const prevTitle = document.title;
    document.title = `BOM-${data.techPack?.label || "Draft"}`;
    window.print();
    document.title = prevTitle;
  };

  // "Printed" time print ke waqt ka ho (Ctrl+P par bhi)
  useEffect(() => {
    const stamp = () =>
      document
        .querySelectorAll(".tp-printed-at")
        .forEach((el) => (el.textContent = printStamp()));
    window.addEventListener("beforeprint", stamp);
    return () => window.removeEventListener("beforeprint", stamp);
  }, []);

  // Agar overview se print button click hua ho to auto-print kare
  useEffect(() => {
    if (props.location?.state?.autoPrint && data.id && !loadingResponce) {
      const timer = setTimeout(() => {
        printSheet();
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [data.id, loadingResponce]);


  // -------------------------------------------------------------------------
  // Row helpers
  // -------------------------------------------------------------------------

  // Jo field edit ho rahi hai us se juda validation error hata dete hain
  const clearRowErrors = (prefix) =>
    setRowErrors((prev) => {
      const keys = Object.keys(prev).filter(
        (key) => key === prefix || key.startsWith(`${prefix}-`),
      );
      if (!keys.length) return prev;
      const next = { ...prev };
      keys.forEach((key) => delete next[key]);
      return next;
    });

  const setField = (name, value) => {
    if (techPackLov.length > 0 && name === "techPack") {
      if (value?.id) {
        getFabricData(value?.id);
      }
    }
    clearRowErrors(name);
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const updateRow = (section, index, patch) => {
    Object.keys(patch).forEach((name) =>
      clearRowErrors(errKey(section, index, name)),
    );
    setData((prev) => ({
      ...prev,
      [section]: prev[section].map((row, i) =>
        i === index ? { ...row, ...patch } : row,
      ),
    }));
  };

  const addRow = (section, factory) => {
    clearRowErrors(section);
    setData((prev) => ({ ...prev, [section]: [...prev[section], factory()] }));
  };

  const removeRow = (section, index) => {
    if (data[section].length <= 1) {
      swal({
        title: "Invalid!",
        text: "Atleast 1 row should be selected!",
        icon: "error",
      });
      return;
    }
    // Row hat gayi to us ke errors bhi hata dein (baaki rows shift ho jati hain)
    clearRowErrors(section);
    setData((prev) => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index),
    }));
  };

  // Est.Days / Quantity / Unit Cost / Rate-per-suit - sab generic path se
  // guzarte hain. Sirf "oheads" ki "qty" ya "unitCost" field badalne par
  // ratePerFg khud-ba-khud (qty * unitCost) recalculate ho jati hai - dono
  // fields ek hi updateRow call me patch hote hain, taake ek extra re-render
  // na ho aur purani/nayi value ka race condition na bane.
  const numberChange = (section, index, name) => (values, sourceInfo) => {
    // Sirf user ke type/clear karne par chale - programmatic value set
    // (jaise form load) par nahi, warna ye load hote hi dobara trigger ho
    // sakta hai.
    if (sourceInfo?.source !== "event") return;

    const val = values?.floatValue !== undefined ? values.floatValue : null;

    if (section === "oheads" && (name === "qty" || name === "unitCost")) {
      const row = data.oheads[index] || {};
      const qty = name === "qty" ? val : row.qty;
      const unitCost = name === "unitCost" ? val : row.unitCost;

      const ratePerFg =
        qty !== null &&
        qty !== undefined &&
        unitCost !== null &&
        unitCost !== undefined
          ? Number(qty) * Number(unitCost)
          : null;

      updateRow(section, index, { [name]: val, ratePerFg });
      return;
    }

    updateRow(section, index, { [name]: val });
  };

  // -------------------------------------------------------------------------
  // Alerts
  // -------------------------------------------------------------------------
  const showError = (error, title = "Error!") => {
    console.error("BOM form error:", error);
    swal({
      title,
      text: getApiErrorMessage(error),
      icon: "error",
    });
  };

  const showWarning = (text, title = "Warning!") => {
    swal({ title, text, icon: "warning" });
  };

  // -------------------------------------------------------------------------
  // Loading
  // -------------------------------------------------------------------------
  useEffect(() => {
    const id = props.location.state?.id ?? props.location?.goBackStates;
    const editData =
      props.location.state?.type || props.location?.state?.updateCheck;

    const permissionStatus = editData === 1 ? 0 : editData === 2 ? 2 : 1;
    checkPermission(ROUTE_FORM, permissionStatus);

    getLovs(id);
  }, []);

  // Saari LOVs ek sath load hoti hain, magar har call apne error ko khud
  // handle karti hai (safePost) - kisi ek LOV ke fail hone par baaki data aur
  // record phir bhi load ho jata hai aur user ko clear warning milti hai.
  const getLovs = async (id) => {
    setLoadingResponce(true);
    const body = { companyId: companyId, username: username };
    const config = { headers: { authorization: token } };

    try {
      const [techPack, component, operation, itemPart, mainItem, item, bom] =
        await Promise.all([
          safePost(API.techPackLov, body, config, "Tech Pack list"),
          safePost(API.componentLov, body, config, "Component list"),
          safePost(API.operationLov, body, config, "Process list"),
          null, //safePost(API.itemPartLov, body, config, "Basis list"),
          safePost(
            API.mainItemLov,
            { check: 1, tables: ["article", "itemPart", "resource"], ...body },
            config,
            "Fabric / Article list",
          ),
          safePost(API.itemLov, body, config, "Item list"),
          id > -1
            ? safePost(API.getById, { id, ...body }, config, "BOM record")
            : Promise.resolve(null),
        ]);

      const serviceTypeOptions = [
        ...new Map(
          operation.res?.data?.accounts.map((row) => [
            row.serv_type_id,
            {
              id: row.serv_type_id,
              value: row.serv_type_id,
              label: row.serv_type_desc,
            },
          ]),
        ).values(),
      ];

      setTechPackLov(asArray(techPack.res?.data?.data));
      setComponentLov(asArray(component.res?.data?.data));
      setOperationLov(asArray(serviceTypeOptions));
      setItemPartLov(asArray(mainItem.res?.data?.itemPart));
      setMainItemLov(asArray(mainItem.res?.data?.article));
      setResourceLov(asArray(mainItem.res?.data?.resource));
      setItemLov(asArray(item.res?.data?.data));

      // Record hi load na ho paye to form bharna bekaar hai - fatal error
      if (bom?.error) {
        showError(bom.error, "Unable to load BOM");
        return;
      }

      // Baaki LOVs me se jo fail hui ho, us ki warning (form phir bhi usable hai)
      const failedLovs = [
        techPack,
        component,
        operation,
        itemPart,
        mainItem,
        item,
      ]
        .filter((r) => r?.error)
        .map((r) => r.label);

      if (failedLovs.length) {
        showWarning(
          `Some lists could not be loaded (${failedLovs.join(
            ", ",
          )}). Please refresh the page before saving.`,
          "Partial Data Loaded",
        );
      }

      if (bom?.res?.data) {
        fillFormData(id, bom.res.data);
      }
    } catch (error) {
      // Safe fallback - safePost/fillFormData me handle na hua koi error
      showError(error, "Unable to load BOM");
    } finally {
      setLoadingResponce(false);
    }
  };

  const handleCreate = (e) => {
    setLoadingResponce(true);
    axios
      .post(
        `${baseurl}/pointOfSale/addresource`,
        {
          resourceName: e,
          companyId: companyId,
          username: username,
        },
        {
          headers: {
            authorization: token,
          },
        },
      )
      .then(function (response) {
        setLoadingResponce(true);
        getLovs();
      })
      .catch(function (error) {
        console.log(error);
      })
      .finally(function () {
        setLoadingResponce(true);
      });
  };
  const getFabricData = async (techPackId) => {
    setLoadingResponce(true);
    await axios
      .post(
        `${baseurl}/pointOfSale/getTechPackWithId`,
        {
          techPackId: techPackId,
          companyId: companyId,
          username: username,
        },
        {
          headers: {
            authorization: token,
          },
        },
      )
      .then(async function (response) {
        setLoadingResponce(false);

        const { dataF, dataT } = response.data;

        let trimStockData = [];
        const currentDate = moment().format("YYYY-MM-DD");
        console.log("currentDate", currentDate);

        if (response.data.dataT.length > 0) {
          trimStockData = await getBulkItemQtyAmt(dataT, 16, currentDate); // 16 warehouse ki id he hard coded Qk form me branch ki lov nhi he
        }
        const stocks = { dataF, trimStockData };
        fillTechFormData(stocks);
      })
      .catch(function (error) {
        console.log(error);
      })
      .finally(function () {
        setLoadingResponce(false);
      });
  };
  const fillTechFormData = (res) => {
    console.log("res", res);

    try {
      const fabrics = (res.dataF || []).map((r) => {
        const fabric = checkNullObject(
          r.fab_main_item_id,
          r.fab_desc ?? r.ITEM_DESC,
        );
        return {
          id: r.tch_pck_fab_id,
          component: checkNullObject(r.compnt_id, r.compnt_desc),
          fabric,
          qtyPerFg: toNum(r.per_suit_cons),
          uom: r.UOM_SHORT_DESC || "",
          rate: toNum(r.stockInfo?.item_Rate),
          remarks: r.remarks || "",
        };
      });

      const trims = (res.trimStockData || []).map((r) => {
        const trim = checkNullObject(r.trim_item_id, r.trim_item_desc);
        return {
          id: r.tch_pck_trim_id,
          item: trim,
          specDet: r.Trim_desc || "",
          qtyPerFg: toNum(r.per_suit_cons),
          uom: r.UOM_SHORT_DESC || "",
          rate: toNum(r.stockInfo?.item_Rate),
          remarks: r.remarks || "",
        };
      });

      setData((prev) => ({
        ...prev,
        fabrics: fabrics.length ? fabrics : [emptyFabRow()],
        trims: trims.length ? trims : [emptyTrimRow()],
      }));
    } catch (error) {
      // Response ka shape expected na ho to screen crash na ho
      console.log(error, "Unable to load Tech Pack data");
    }
  };

  // Response shape:
  //   data     -> [master]     (st_bom_mt + linked Tech Pack info)
  //   dataF  -> fabrics      (st_bom_fab)
  //   dataT -> trims        (st_bom_trims)
  //   dataO  -> cost heads   (st_bom_oheads)
  const fillFormData = (id, res) => {
    try {
      const master = res?.data?.[0];
      if (!master) {
        showWarning(
          "BOM record not found. Please go back to the overview and try again.",
          "Record Not Found",
        );
        return;
      }

      const fabrics = asArray(res?.dataF).map((r) => {
        return {
          id: r.bom_fab_id,
          component: checkNullObject(r.compnt_id, r.compnt_desc),
          fabric: checkNullObject(r.fab_main_item_id, r.fab_desc),
          qtyPerFg: toNum(r.qty_per_fg),
          uom: r.UOM_SHORT_DESC ?? "",
          rate: toNum(r.rate),
        };
      });

      const trims = asArray(res?.dataT).map((r) => {
        return {
          id: r.bom_trm_id,
          item: checkNullObject(r.item_id, r.item_desc),
          specDet: r.spec_det || "",
          qtyPerFg: toNum(r.qty_per_fg),
          uom: r.UOM_SHORT_DESC ?? "",
          rate: toNum(r.rate),
        };
      });

 
      const oheads = asArray(res?.dataO).map((r) => {
  const itemPartArray = r?.item_part_info
    ? JSON.parse(r.item_part_info)
    : [];

  return {
    id: r.bom_ohd_id,
    operation: checkNullObject(r.serv_type_id, r.serv_type_desc),
    itemPart: itemPartArray,
    component: checkNullObject(r.compnt_id, r.compnt_desc),
    resource: checkNullObject(r.resource_id, r.resource_desc),
    estDays: toNum(r.est_days),
    qty: toNum(r.qty),
    unitCost: toNum(r.unit_cost),
    ratePerFg: toNum(r.rate_per_fg),
  };
});
      const remarksList = (res.dataR || []).map((r) => ({
        id: r.bom_remarks_id,
        remarks: r.bom_remarks_desc || "",
      }));
      setData({
        id,
        techPack: (() => {
          const base = checkNullObject(master.bom_id, master.article);
          return base
            ? {
                ...base,
                ver_no: master.ver_no,
                no_of_pcs: toNum(master.no_of_pcs),
              }
            : null;
        })(),
        fabrics: fabrics.length ? fabrics : [emptyFabRow()],
        trims: trims.length ? trims : [emptyTrimRow()],
        oheads: oheads.length ? oheads : [emptyOheadRow()],
        remarksList: remarksList.length ? remarksList : [emptyRemarksRow()],
      });
    } catch (error) {
      // Response ka shape expected na ho to screen crash na ho
      showError(error, "Unable to load BOM");
    }
  };

  // -------------------------------------------------------------------------
  // Totals (client-side only - DB me amount store nahi hota)
  // -------------------------------------------------------------------------
  const plannedQty = toNum(data.techPack?.no_of_pcs) || 0;

  const fabricTotal = data.fabrics.reduce(
    (sum, r) => sum + rowAmount(r.qtyPerFg, r.rate),
    0,
  );
  const trimTotal = data.trims.reduce(
    (sum, r) => sum + rowAmount(r.qtyPerFg, r.rate),
    0,
  );
  // Cost heads ki rate seedhi "per suit" hoti hai (koi consumption factor nahi)
  const oheadRows = data?.oheads.map((r) => ({
    ...r,
    amountPerSuit: toNum(r.ratePerFg) || 0,
  }));
  const oheadTotal = oheadRows.reduce((sum, r) => sum + r.amountPerSuit, 0);

  const grandTotalPerSuit = fabricTotal + trimTotal + oheadTotal;

  // -------------------------------------------------------------------------
  // Save
  // -------------------------------------------------------------------------
  // Har invalid field ka key banta hai (red border ke lie) aur message row
  // number ke saath - take user ko exact pata chale kahan ghalti hai.
  const validate = () => {
    const errors = {};
    const problems = [];
    const MAX_MESSAGES = 4;

    const push = (key, message) => {
      errors[key] = true;
      problems.push(message);
    };

    if (!optId(data.techPack))
      push("techPack", "Tech Pack (Article • Version) is required.");

    data.fabrics.forEach((r, i) => {
      const rowNo = i + 1;
      if (!optId(r.component))
        push(
          errKey("fabrics", i, "component"),
          `Raw Fabric row ${rowNo}: Component is required.`,
        );
      if (!optId(r.fabric))
        push(
          errKey("fabrics", i, "fabric"),
          `Raw Fabric row ${rowNo}: Fabric Stuff is required.`,
        );
      if (!toNum(r.qtyPerFg) || toNum(r.qtyPerFg) <= 0)
        push(
          errKey("fabrics", i, "qtyPerFg"),
          `Raw Fabric row ${rowNo}: Planned Consumption should be greater than 0.`,
        );
    });

    data.trims.forEach((r, i) => {
      const rowNo = i + 1;
      if (!optId(r.item))
        push(
          errKey("trims", i, "item"),
          `Trims / Accessories row ${rowNo}: Material is required.`,
        );
      if (!toNum(r.qtyPerFg) || toNum(r.qtyPerFg) <= 0)
        push(
          errKey("trims", i, "qtyPerFg"),
          `Trims / Accessories row ${rowNo}: Planned Consumption should be greater than 0.`,
        );
    });

    data.oheads.forEach((r, i) => {
      if (!optId(r.operation))
        push(
          errKey("oheads", i, "operation"),
          `Cost Head row ${i + 1}: Cost Head is required.`,
        );
    });

    if (!data.remarksList.some((r) => r.remarks?.trim())) {
      push(
        errKey("remarksList", 0, "remarks"),
        "Remarks: At least one row must contain remarks.",
      );
    }

    if (!problems.length) return null;

    const shown = problems.slice(0, MAX_MESSAGES);
    const remaining = problems.length - shown.length;

    return {
      errors,
      message: `${shown.join(" ")}${remaining > 0 ? ` (+${remaining} more)` : ""}`,
    };
  };

  const buildPayload = () => ({
    id: data.id,
    techPackId: optId(data.techPack),
    fabrics: data.fabrics.map((r) => ({
      id: r.id,
      compntId: optId(r.component),
      fabMainItemId: optId(r.fabric),
      qtyPerFg: r.qtyPerFg,
      rate: r.rate,
    })),
    trims: data.trims.map((r) => ({
      id: r.id,
      itemId: optId(r.item),
      specDet: r.specDet,
      qtyPerFg: r.qtyPerFg,
      rate: r.rate,
    })),
    oheads: data.oheads.map((r) => ({
      id: r.id,
      operationId: optId(r.operation),
      componentId: optId(r.component),
      itemPartId: r.itemPart,
      resourceId: optId(r.resource),
      estDays: r.estDays,
      qty: r.qty,
      unitCost: r.unitCost,
      ratePerFg: r.ratePerFg,
    })),
    remarks: data.remarksList
      .filter((r) => r.remarks?.trim())
      .map((r) => ({
        id: r.id,
        remarks: r.remarks.trim(),
      })),
  });

  const handleSaveDraft = async (event) => {
    event.preventDefault();

    // Loader chal raha ho ya form lock ho to dobara submit na ho
    if (loadingResponce || isLocked) return;

    const invalid = validate();
    if (invalid) {
      setRowErrors(invalid.errors);
      swal({ title: "Invalid!", text: invalid.message, icon: "error" });
      return;
    }
    setRowErrors({});
    setLoadingResponce(true);

    try {
      const payload = buildPayload();

      const response = await axios.post(
        isEdit ? API.update : API.add,
        { companyId: companyId, username: username, ...payload },
        { headers: { authorization: token } },
      );

      console.log("BOM save response:", response?.data);
      const res = response?.data || {};

      // 1) Backend ka duplicate BOM flag
      if (res.duplicate === true) {
        swal({
          title: "Duplicate Entry",
          text: res.message || "This Tech Pack already has a BOM.",
          icon: "error",
        });
        return;
      }

      // 2) Backend ne HTTP 200 ke saath success:false / error bheja ho
      if (res.success === false || res.error) {
        showError({ response: { data: res } }, "BOM Not Saved");
        return;
      }

      // 3) Success
      setIsSavedLocked(true);
      if (!isEdit && (res.id ?? res.bom_id)) {
        setData((prev) => ({ ...prev, id: res.id ?? res.bom_id }));
      }
      swal({
        title: "Success!",
        text: isEdit ? "BOM Updated!" : "BOM Saved!",
        icon: "success",
      });
    } catch (error) {
      // Network / 4xx / 5xx - sab yahan handle hota hai
      showError(error, isEdit ? "Unable to Update BOM" : "Unable to Save BOM");
    } finally {
      setLoadingResponce(false);
    }
  };

  // -------------------------------------------------------------------------
  // UI
  // -------------------------------------------------------------------------
  return (
    <>
      {loadingResponce ? (
        <ResponseLoader />
      ) : (
        <div>
          <style>{PRINT_CSS}</style>
          <div className="tp-no-print">
            <Row className="Creative-Invoice-header-styling">
              <Col xs="6" className="card-header-Creative-Invoice">
                <strong>
                  <h4>
                    BOM &amp; Pre-Cost
                    {data.techPack ? ` — ${data.techPack.label}` : ""}
                  </h4>
                </strong>
                {data.techPack && (
                  <small className="text-muted">
                    Tech Pack v{data.techPack.ver_no} • Planned Suit Qty{" "}
                    {plannedQty || "—"}
                  </small>
                )}
              </Col>
              <Col xs="6" className="card-header-Creative-Invoice">
                <div style={{ textAlign: "right" }}>
                  {(isViewOnly || isEdit) && (
                    <Button
                      type="button"
                      size="sm"
                      color="primary"
                      outline
                      title="Print"
                      onClick={printSheet}
                      className="mr-2"
                    >
                      <i className="fa fa-print mr-1"></i>
                      Print
                    </Button>
                  )}
                  <Link
                    to={ROUTE_OVERVIEW}
                    style={{
                      fontFamily: "Times New Roman",
                      fontWeight: "normal",
                      margin: "10px",
                    }}
                  >
                    <u className="Back-to-Overview">Back to Overview</u>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      fill="currentColor"
                      className="bi bi-chevron-left mx-2 icon-color"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fillRule="evenodd"
                        d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
                      />
                    </svg>
                  </Link>
                </div>
              </Col>
            </Row>

            <Row className="rowStyling">
              <Col>
                <Form name="simpleForm" onSubmit={handleSaveDraft}>
                  {/* ---------------- VALIDATION SUMMARY ---------------- */}
                  {Object.keys(rowErrors).length > 0 && (
                    <div className="alert alert-danger py-2 mb-2" role="alert">
                      <i className="fa fa-exclamation-triangle mr-2"></i>
                      Some required fields are missing / invalid. Please fix the
                      highlighted fields below and save again.
                    </div>
                  )}

                  {/* ---------------- IDENTITY ---------------- */}
                  <h6 style={SECTION_TITLE_STYLE}>Identity</h6>
                  <div className="row">
                    <div className="col-md-4" style={{ marginBottom: "12px" }}>
                      <Label style={{ fontWeight: "bold" }}>
                        Tech Pack (Article • Version):
                        <h8 style={{ color: "tomato" }}>*</h8>
                      </Label>
                      <Select
                        //isClearable
                        value={data.techPack || ""}
                        options={techPackLov}
                        onChange={(e) => setField("techPack", e)}
                        isOptionDisabled={(opt) => !!opt.bom_id && !isEdit}
                        isDisabled={isLocked || isEdit}
                        name="techPack"
                        placeholder={"-- Select --"}
                        styles={selectErrorStyles(rowErrors.techPack)}
                      />
                    </div>
                    <div className="col-md-2" style={{ marginBottom: "12px" }}>
                      <Label style={{ fontWeight: "bold" }}>
                        Multi Remarks:
                      </Label>
                      <div>
                        <Button
                          type="button"
                          color="secondary"
                          outline
                          onClick={() => setRemarksModalOpen(true)}
                        >
                          <i className="fa fa-comment-dots mr-1"></i>{" "}
                          {data.remarksList?.filter((r) => r.remarks?.trim())
                            .length
                            ? `${data.remarksList.filter((r) => r.remarks?.trim()).length} Remark(s)`
                            : "Add Remarks"}
                        </Button>
                      </div>
                    </div>
                    {/*  <div className="col-md-3" style={{ marginBottom: "12px" }}>
                    <Label style={{ fontWeight: "bold" }}>Planned Suit Qty:</Label>
                    <Input value={plannedQty || ""} disabled readOnly />
                  </div>*/}
                  </div>

                  {/* ---------------- REMARKS MODAL ---------------- */}
                  <Modal
                    show={remarksModalOpen}
                    onHide={() => setRemarksModalOpen(false)}
                    centered
                  >
                    <Modal.Header closeButton>
                      <Modal.Title>Remarks</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <table className="table table-sm mb-0">
                        <tbody>
                          {data.remarksList.map((row, index) => (
                            <tr key={index}>
                              <td>
                                <Input
                                  type="textarea"
                                  row="1"
                                  value={row.remarks || ""}
                                  onChange={(e) =>
                                    updateRow("remarksList", index, {
                                      remarks: e.target.value,
                                    })
                                  }
                                  disabled={isLocked}
                                  placeholder="Enter remark"
                                  name="remarks"
                                />
                              </td>
                              {!isLocked && (
                                <RowActions
                                  onAdd={() =>
                                    addRow("remarksList", emptyRemarksRow)
                                  }
                                  onRemove={() =>
                                    removeRow("remarksList", index)
                                  }
                                />
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Modal.Body>
                    <Modal.Footer>
                      <Button
                        color="primary"
                        type="button"
                        onClick={() => setRemarksModalOpen(false)}
                      >
                        Done
                      </Button>
                    </Modal.Footer>
                  </Modal>

                  {/* ---------------- RAW FABRIC ---------------- */}
                  <h6 style={SECTION_TITLE_STYLE}>Raw Fabric</h6>
                  <div className="InvoiceLine-styling">
                    <table className="table table-sm table-striped mb-2">
                      <thead>
                        <tr style={HEAD_STYLE}>
                          <th style={{ minWidth: "180px" }}>Component</th>
                          <th style={{ minWidth: "220px" }}>Fabric Stuff</th>
                          <th style={{ minWidth: "120px" }}>
                            Planned Cons / Suit
                          </th>
                          <th style={{ minWidth: "70px" }}>UOM</th>
                          <th style={{ minWidth: "100px" }}>Rate</th>
                          <th style={{ minWidth: "110px" }}>Planned Amount</th>
                          {!isLocked && <th></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {data.fabrics.map((row, index) => (
                          <tr key={index}>
                            <td>
                              <Select
                                isClearable
                                value={row.component || ""}
                                options={componentLov}
                                onChange={(e) =>
                                  updateRow("fabrics", index, { component: e })
                                }
                                isDisabled //={isLocked}
                                name="component"
                                placeholder={"-- Select --"}
                                styles={selectErrorStyles(
                                  rowErrors[
                                    errKey("fabrics", index, "component")
                                  ],
                                )}
                              />
                            </td>
                            <td>
                              <Select
                                isClearable
                                value={row.fabric || ""}
                                options={mainItemLov}
                                onChange={(e) =>
                                  updateRow("fabrics", index, {
                                    fabric: e,
                                    uom: e?.UOM_SHORT_DESC || "",
                                  })
                                }
                                isDisabled //={isLocked}
                                name="fabric"
                                placeholder={"-- Select --"}
                                styles={selectErrorStyles(
                                  rowErrors[errKey("fabrics", index, "fabric")],
                                )}
                              />
                            </td>
                            <td>
                              <NumberFormat
                                name="qtyPerFg"
                                className="form-control"
                                style={{
                                  textAlign: "right",
                                  ...fieldErrorStyle(
                                    rowErrors[
                                      errKey("fabrics", index, "qtyPerFg")
                                    ],
                                  ),
                                }}
                                thousandSeparator={true}
                                value={row.qtyPerFg ?? ""}
                                decimalScale={3}
                                allowNegative={false}
                                placeholder="0.000"
                                onValueChange={numberChange(
                                  "fabrics",
                                  index,
                                  "qtyPerFg",
                                )}
                                required
                                disabled //={isLocked}
                              />
                            </td>
                            <td>
                              <Input value={row.uom || ""} disabled readOnly />
                            </td>
                            <td>
                              <NumberFormat
                                name="rate"
                                className="form-control"
                                style={{ textAlign: "right" }}
                                thousandSeparator={true}
                                prefix="Rs. "
                                value={row.rate ?? ""}
                                decimalScale={2}
                                allowNegative={false}
                                placeholder="Rs. 0.00"
                                onValueChange={numberChange(
                                  "fabrics",
                                  index,
                                  "rate",
                                )}
                                disabled={isLocked}
                              />
                            </td>
                            <td className="text-right">
                              Rs. {fmtMoney(rowAmount(row.qtyPerFg, row.rate))}
                            </td>
                            {/* {!isLocked && (
                            <RowActions
                              onAdd={() => addRow("fabrics", emptyFabRow)}
                              onRemove={() => removeRow("fabrics", index)}
                            />
                          )} */}
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={isLocked ? 5 : 6} className="text-right">
                            <strong>Sub-total — Raw Fabric</strong>
                          </td>
                          <td colSpan={isLocked ? 1 : 1} className="text-right">
                            <strong>Rs. {fmtMoney(fabricTotal)}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* ---------------- TRIMS / ACCESSORIES ---------------- */}
                  <h6 style={SECTION_TITLE_STYLE}>Trims / Accessories</h6>
                  <div className="InvoiceLine-styling">
                    <table className="table table-sm table-striped mb-2">
                      <thead>
                        <tr style={HEAD_STYLE}>
                          <th style={{ minWidth: "180px" }}>Material</th>
                          <th style={{ minWidth: "220px" }}>Specification</th>
                          <th style={{ minWidth: "120px" }}>
                            Planned Cons / Suit
                          </th>
                          <th style={{ minWidth: "70px" }}>UOM</th>
                          <th style={{ minWidth: "100px" }}>Rate</th>
                          <th style={{ minWidth: "110px" }}>Planned Amount</th>
                          {!isLocked && <th></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {data.trims.map((row, index) => (
                          <tr key={index}>
                            <td>
                              <Select
                                isClearable
                                value={row.item || ""}
                                options={itemLov}
                                onChange={(e) =>
                                  updateRow("trims", index, {
                                    item: e,
                                    uom: e?.UOM_SHORT_DESC || "",
                                  })
                                }
                                isDisabled //={isLocked}
                                name="item"
                                placeholder={"-- Select --"}
                                styles={selectErrorStyles(
                                  rowErrors[errKey("trims", index, "item")],
                                )}
                              />
                            </td>
                            <td>
                              <Input
                                value={row.specDet || ""}
                                onChange={(e) =>
                                  updateRow("trims", index, {
                                    specDet: e.target.value,
                                  })
                                }
                                maxLength={100}
                                disabled //={isLocked}
                                name="specDet"
                              />
                            </td>
                            <td>
                              <NumberFormat
                                name="qtyPerFg"
                                className="form-control"
                                style={{
                                  textAlign: "right",
                                  ...fieldErrorStyle(
                                    rowErrors[
                                      errKey("trims", index, "qtyPerFg")
                                    ],
                                  ),
                                }}
                                thousandSeparator={true}
                                value={row.qtyPerFg ?? ""}
                                decimalScale={3}
                                allowNegative={false}
                                placeholder="0.000"
                                onValueChange={numberChange(
                                  "trims",
                                  index,
                                  "qtyPerFg",
                                )}
                                required
                                disabled //={isLocked}
                              />
                            </td>
                            <td>
                              <Input value={row.uom || ""} disabled readOnly />
                            </td>
                            <td>
                              <NumberFormat
                                name="rate"
                                className="form-control"
                                style={{ textAlign: "right" }}
                                thousandSeparator={true}
                                prefix="Rs. "
                                value={row.rate ?? ""}
                                decimalScale={2}
                                allowNegative={false}
                                placeholder="Rs. 0.00"
                                onValueChange={numberChange(
                                  "trims",
                                  index,
                                  "rate",
                                )}
                                disabled={isLocked}
                              />
                            </td>
                            <td className="text-right">
                              Rs. {fmtMoney(rowAmount(row.qtyPerFg, row.rate))}
                            </td>
                            {/* {!isLocked && (
                            <RowActions
                              onAdd={() => addRow("trims", emptyTrimRow)}
                              onRemove={() => removeRow("trims", index)}
                            />
                          )} */}
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={isLocked ? 5 : 6} className="text-right">
                            <strong>Sub-total — Trims / Accessories</strong>
                          </td>
                          <td className="text-right">
                            <strong>Rs. {fmtMoney(trimTotal)}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* ---------------- PROCESS COST HEADS ---------------- */}
                  <h6 style={SECTION_TITLE_STYLE}>
                    Process Cost Heads (Planned Rate / Suit)
                  </h6>
                  <div className="InvoiceLine-styling">
                    <table className="table table-sm table-striped mb-2">
                      <thead>
                        <tr style={HEAD_STYLE}>
                          <th style={{ minWidth: "180px" }}>Operation</th>
                          <th style={{ minWidth: "180px" }}>Item Part</th>
                          <th style={{ minWidth: "180px" }}>Component</th>
                          <th style={{ minWidth: "180px" }}>Resource</th>
                          <th style={{ minWidth: "120px" }}>Est.Days (LOT)</th>
                          <th style={{ minWidth: "120px" }}>Quantity</th>
                          <th style={{ minWidth: "120px" }}>Unit Cost</th>
                          <th style={{ minWidth: "120px" }}>Plan.Amt / Suit</th>
                          <th style={{ minWidth: "120px" }}>
                            Plan.Amt (× {plannedQty || 0})
                          </th>
                          {!isLocked && <th></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {oheadRows.map((row, index) => (
                          <tr key={index}>
                            <td>
                              <Select
                                isClearable
                                value={row.operation || ""}
                                options={operationLov}
                                onChange={(e) =>
                                  updateRow("oheads", index, { operation: e })
                                }
                                isDisabled={isLocked}
                                name="operation"
                                placeholder={"-- Select --"}
                                styles={selectErrorStyles(
                                  rowErrors[
                                    errKey("oheads", index, "operation")
                                  ],
                                )}
                              />
                            </td>
                            <td>
                              <Select
                                isMulti 
                                isClearable
                                value={row.itemPart || []}
                                options={itemPartLov} 
                                onChange={(e) =>
                                  updateRow("oheads", index, {
                                    itemPart: e || [],
                                  })
                                }
                                isDisabled={isLocked}
                                name="itemPart"
                                placeholder={"-- Select --"}
                              />
                            </td>
                            <td>
                              <Select
                                isClearable
                                value={row.component || ""}
                                options={componentLov}
                                onChange={(e) =>
                                  updateRow("oheads", index, { component: e })
                                }
                                isDisabled ={isLocked}
                                name="component"
                                placeholder={"-- Select --"}
                                styles={selectErrorStyles(
                                  rowErrors[
                                    errKey("oheads", index, "component")
                                  ],
                                )}
                              />
                            </td>
                            <td>
                              <CreatableSelect
                                isClearable
                                value={row.resource || ""}
                                options={resourceLov}
                                onChange={(e) =>
                                  updateRow("oheads", index, { resource: e })
                                }
                                isDisabled={isLocked}
                                name="resource"
                                onCreateOption={(e) => handleCreate(e)}
                                placeholder={"-- Select --"}
                              />
                            </td>
                            <td>
                              <NumberFormat
                                name="estDays"
                                className="form-control"
                                style={{ textAlign: "right" }}
                                thousandSeparator={true}
                                // prefix="Rs. "
                                value={row.estDays ?? ""}
                                decimalScale={2}
                                allowNegative={false}
                                placeholder="0.00"
                                onValueChange={numberChange(
                                  "oheads",
                                  index,
                                  "estDays",
                                )}
                                disabled={isLocked}
                              />
                            </td>
                            <td>
                              <NumberFormat
                                name="qty"
                                className="form-control"
                                style={{ textAlign: "right" }}
                                thousandSeparator={true}
                                // prefix="Rs. "
                                value={row.qty ?? ""}
                                decimalScale={2}
                                allowNegative={false}
                                placeholder="0.00"
                                onValueChange={numberChange(
                                  "oheads",
                                  index,
                                  "qty",
                                )}
                                disabled={isLocked}
                              />
                            </td>
                            <td>
                              <NumberFormat
                                name="unitCost"
                                className="form-control"
                                style={{ textAlign: "right" }}
                                thousandSeparator={true}
                                prefix="Rs. "
                                value={row.unitCost ?? ""}
                                decimalScale={2}
                                allowNegative={false}
                                placeholder="Rs. 0.00"
                                onValueChange={numberChange(
                                  "oheads",
                                  index,
                                  "unitCost",
                                )}
                                disabled={isLocked}
                              />
                            </td>
                            <td>
                              <NumberFormat
                                name="ratePerFg"
                                className="form-control"
                                style={{ textAlign: "right" }}
                                thousandSeparator={true}
                                prefix="Rs. "
                                value={row.ratePerFg ?? ""}
                                decimalScale={2}
                                allowNegative={false}
                                placeholder="Rs. 0.00"
                                onValueChange={numberChange(
                                  "oheads",
                                  index,
                                  "ratePerFg",
                                )}
                                disabled //={isLocked}
                                readOnly
                              />
                            </td>
                            <td className="text-left">
                              Rs. {fmtMoney(row.amountPerSuit * plannedQty)}
                            </td>
                            {!isLocked && (
                              <RowActions
                                onAdd={() => addRow("oheads", emptyOheadRow)}
                                onRemove={() => removeRow("oheads", index)}
                              />
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* ---------------- PRE-COST SUMMARY ---------------- */}
                  <div className="row">
                    <div className="col-md-6"></div>
                    <div className="col-md-6">
                      <div className="InvoiceLine-styling">
                        <h6 style={{ color: "#1f3864", fontWeight: "bold" }}>
                          Pre-Cost Summary (Planned Suit Qty: {plannedQty || 0})
                        </h6>
                        <table className="table table-sm mb-0">
                          <thead>
                            <tr style={HEAD_STYLE}>
                              <th>Cost Head</th>
                              <th className="text-right">Amount / Suit</th>
                              <th className="text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>Raw Fabric</td>
                              <td className="text-right">
                                Rs. {fmtMoney(fabricTotal)}
                              </td>
                              <td className="text-right">
                                Rs. {fmtMoney(fabricTotal * plannedQty)}
                              </td>
                            </tr>
                            <tr>
                              <td>Trims / Accessories</td>
                              <td className="text-right">
                                Rs. {fmtMoney(trimTotal)}
                              </td>
                              <td className="text-right">
                                Rs. {fmtMoney(trimTotal * plannedQty)}
                              </td>
                            </tr>
                            {oheadRows
                              .filter((r) => r.operation)
                              .map((r, i) => (
                                <tr key={i}>
                                  <td>{r.operation.label}</td>
                                  <td className="text-right">
                                    Rs. {fmtMoney(r.amountPerSuit)}
                                  </td>
                                  <td className="text-right">
                                    Rs. {fmtMoney(r.amountPerSuit * plannedQty)}
                                  </td>
                                </tr>
                              ))}
                            <tr>
                              <td>
                                <strong>Grand Total (Pre-Cost)</strong>
                              </td>
                              <td className="text-right">
                                <strong>
                                  Rs. {fmtMoney(grandTotalPerSuit)}
                                </strong>
                              </td>
                              <td className="text-right">
                                <strong>
                                  Rs. {fmtMoney(grandTotalPerSuit * plannedQty)}
                                </strong>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <hr
                    style={{
                      border: "1px solid ",
                      marginBottom: "3vh",
                      marginTop: "1vh",
                      borderColor: "E4E5E6",
                    }}
                  />

                  {/* ---------------- BUTTONS ---------------- */}
                  <div className="col-md-12 d-flex flex-row-reverse">
                    <FormGroup>
                      <Button
                        className="mr-1 invoiceSubmitButton "
                        type="submit"
                        hidden={isLocked}
                        disabled={loadingResponce}
                      >
                        {loadingResponce
                          ? "Saving..."
                          : isEdit
                            ? "Update Changes"
                            : "Save Changes"}
                      </Button>
                      {(isViewOnly || isEdit) && (
                        <Button
                          type="button"
                          size="sm"
                          color="primary"
                          outline
                          title="Print"
                          onClick={printSheet}
                        >
                          <i className="fa fa-print mr-1"></i>
                          Print
                        </Button>
                      )}
                      <Link to={{ pathname: ROUTE_OVERVIEW }}>
                        <Button className="mr-1" size="sm" type="button">
                          Cancel
                        </Button>
                      </Link>
                    </FormGroup>
                  </div>
                </Form>
              </Col>
            </Row>
          </div>
          <PrintSheet
            data={data}
            oheadRows={oheadRows}
            plannedQty={plannedQty}
            fabricTotal={fabricTotal}
            trimTotal={trimTotal}
            grandTotalPerSuit={grandTotalPerSuit}
          />
        </div>
      )}
    </>
  );
}
